import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input, Checkbox, PhoneInput, AddressInputController, geocodeAddress } from '@shopmuchmore/design-system';
import { shippingAddressSchema, type ShippingAddressFormData } from '../shipping.schemas';
import type { CreateShippingAddressInput } from '../shipping.types';

interface ShippingAddressFormProps {
  initialData?: Partial<ShippingAddressFormData>;
  onSubmit: (data: CreateShippingAddressInput) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ShippingAddressForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}: ShippingAddressFormProps) {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ShippingAddressFormData>({
    resolver: zodResolver(shippingAddressSchema),
    defaultValues: {
      first_name: initialData?.first_name || '',
      last_name: initialData?.last_name || '',
      email: initialData?.email || '',
      phone_number: initialData?.phone_number || '',
      street_address: initialData?.street_address || '',
      city: initialData?.city || '',
      postal_code: initialData?.postal_code || '',
      state_or_region: initialData?.state_or_region || '',
      country: initialData?.country || '',
      latitude: initialData?.latitude ?? null,
      longitude: initialData?.longitude ?? null,
      is_default: initialData?.is_default || false,
    },
  });

  useEffect(() => {
    if (initialData) {
      reset({
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        email: initialData.email || '',
        phone_number: initialData.phone_number || '',
        street_address: initialData.street_address || '',
        city: initialData.city || '',
        postal_code: initialData.postal_code || '',
        state_or_region: initialData.state_or_region || '',
        country: initialData.country || '',
        latitude: initialData.latitude ?? null,
        longitude: initialData.longitude ?? null,
        is_default: initialData.is_default || false,
      });
    }
  }, [initialData, reset]);

  const handleFormSubmit = async (data: ShippingAddressFormData) => {
    let lat = data.latitude;
    let lng = data.longitude;

    if ((lat === undefined || lat === null || lng === undefined || lng === null) && data.street_address) {
      const fullAddressString = [
        data.street_address,
        data.city,
        data.state_or_region,
        data.postal_code,
        data.country,
      ]
        .filter(Boolean)
        .join(', ');
      const coords = await geocodeAddress({ address: fullAddressString });
      if (coords) {
        lat = coords.lat;
        lng = coords.lng;
      }
    }

    const sanitizeCoord = (val?: number | string | null) => {
      if (val === undefined || val === null || val === '') return null;
      const num = Number(val);
      return isNaN(num) ? null : Number(num.toFixed(6));
    };

    // Map form data to API input (Omit shipping_slug which is system-managed)
    const payload: CreateShippingAddressInput = {
      ...data,
      latitude: sanitizeCoord(lat),
      longitude: sanitizeCoord(lng),
      is_default: data.is_default ?? false,
    };
    await onSubmit(payload);
  };

  const inputClasses = "bg-white border-[1.5px] border-gray-100 rounded-xl focus:border-secondary transition-all duration-200 h-12";

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Name */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700 ml-1">First Name</label>
          <Input
            {...register('first_name')}
            placeholder="John"
            disabled={isLoading}
            className={inputClasses}
          />
          {errors.first_name && (
            <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.first_name.message}</p>
          )}
        </div>

        {/* Last Name */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700 ml-1">Last Name</label>
          <Input
            {...register('last_name')}
            placeholder="Doe"
            disabled={isLoading}
            className={inputClasses}
          />
          {errors.last_name && (
            <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.last_name.message}</p>
          )}
        </div>
      </div>

        {/* Email */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700 ml-1">Email Address</label>
          <Input
            {...register('email')}
            type="email"
            placeholder="john.doe@example.com"
            disabled={isLoading}
            className={inputClasses}
          />
          {errors.email && (
            <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.email.message}</p>
          )}
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700 ml-1">Phone Number</label>
          <Controller
            name="phone_number"
            control={control}
            render={({ field: { onChange, value } }) => (
              <PhoneInput
                placeholder="+44 7911 123456"
                value={value}
                onChange={(val: any) => onChange(val || "")}
                disabled={isLoading}
                defaultCountry="GB"
                international
                className={inputClasses}
              />
            )}
          />
          {errors.phone_number && (
            <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.phone_number.message}</p>
          )}
        </div>


      {/* Line-by-Line Address Input */}
        <AddressInputController
          name="street_address"
          control={control}
          setValue={setValue}
          getValues={getValues}
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}
          label="Address"
          placeholder="Street address, flat/building/suite number"
          countries={['gb']}
          autocomplete={false}
          error={
            errors.street_address?.message ||
            errors.city?.message ||
            errors.postal_code?.message ||
            errors.state_or_region?.message ||
            errors.country?.message
          }
          required
        />

      {/* Default Checkbox */}
      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border-[1.5px] border-gray-100 transition-colors hover:bg-gray-100/50">
        <Controller
          name="is_default"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              id="is_default"
              checked={value}
              onCheckedChange={onChange}
              disabled={isLoading}
              className="w-5 h-5"
            />
          )}
        />
        <label htmlFor="is_default" className="text-sm font-bold text-gray-900 cursor-pointer flex-1">
          Set as default shipping address
        </label>
      </div>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row items-center sm:justify-end gap-4 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          className="w-full sm:w-auto min-w-[120px] h-12 font-bold rounded-xl border-gray-200 text-gray-600 order-2 sm:order-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          loading={isLoading}
          disabled={isLoading}
          className="w-full sm:w-auto min-w-[160px] h-12 font-bold rounded-xl order-1 sm:order-2"
        >
          {initialData ? 'Update Address' : 'Save Address'}
        </Button>
      </div>
    </form>
  );
}
